// ─────────────────────────────────────────────
//  resolveIdentity — who is this, and how do we know?
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  THE MEASURED PROBLEM. Staff matching on the import path is exact normalised
//  string equality. The sheet writes `Dave Cadell`, `Liz Lucaj`, `Dimi
//  Borissova`, `Chip Compton`; the roster holds David, Elizabeth, Dimitrina,
//  Kang. 19 of the 62 humans named on the live sheet of 2026-08-20 matched
//  nobody — and on a miss the importer CREATED a staff record, which is how 23
//  duplicate colleagues appeared in two days.
//
//  THE LADDER, highest confidence first. Every rung reports HOW it matched, so
//  a reviewer can see the evidence rather than a bare id:
//
//    1. alias     — a human already answered this exact string (staff_aliases)
//    2. qgenda    — the workbook's own `Qgenda Report` says the printed name
//                   "Chip Compton" is `Compton, Kang`
//    3. exact     — normalised full-name equality (today's whole algorithm)
//    4. nickname  — the shipped canonicalPerson table: Bob→Robert, Jim→James
//    5. prefix    — surname + a given-name PREFIX: Dom→Dominique, Dimi→Dimitrina
//    6. surnameInitial — a given name in full + a surname abbreviated to ONE
//                   letter: `Kayla G` → Kayla Gibbons. How this sheet writes
//                   every orientee.
//    ─ below that → UNRESOLVED, named, and handed to a person.
//
//  ⚠️ THIS FUNCTION NEVER CREATES A PERSON. It returns a match or it returns
//  "unresolved". Creating on a miss is the defect, not the fallback.
//
//  ⚠️ IT NEVER PICKS BETWEEN TWO CANDIDATES. Two people share a surname —
//  Casey and Elizabeth Bennett, Bhavin and Shivam Patel, five Samanthas — and a
//  confident wrong answer puts the wrong clinician in a room with a patient.
//  Ambiguity is an OUTCOME (`reason: "ambiguous"`) that names every candidate,
//  the same refusal the timeline's name menu already makes on a duplicated
//  name. The ONE exception is spelled out on `pickOne` below and is not a
//  choice between two people.
//
//  ⚠️ NO NEW NORMALISER LIVES HERE. `normalizeName` (team-directory) is the
//  join key this app already uses, and `sameHuman`/`nameParts` (canonicalPerson)
//  are the shipped nickname vocabulary. This repo has been bitten repeatedly by
//  a second copy of a vocabulary drifting from the first; the whole value of a
//  rung is that it agrees with the rest of the app about what a name is.
//
//  Pure — no Firebase, no React, no network. The caller loads the staff pool,
//  the alias docs and (optionally) the parsed QGenda index and passes them in.
// ─────────────────────────────────────────────

import { normalizeName } from "../features/team-directory/normalizeName";
import { nameParts, sameHuman, surnameOf } from "./canonicalPerson";

// How much a rung is worth. Not a probability — an ORDERING that a review queue
// can threshold on, and that a reviewer can read as "how was this decided".
export const IDENTITY_CONFIDENCE = {
  alias: 1,
  qgenda: 0.95,
  exact: 0.9,
  nickname: 0.75,
  prefix: 0.6,
  surnameInitial: 0.5,
};

// The shortest given-name prefix rung 5 will accept. Two letters would make
// `Jo` answer for Joseph, Joshua, Joanna and John; three is the shortest form
// this department actually writes (`Dom`, `Kim`, `Jen`, `Sam`, `Tim`), and it
// still refuses whenever more than one colleague fits.
const MIN_PREFIX = 3;

const norm = (s) => normalizeName(s);
const first = (name) => (nameParts(name)[0] || "");

/**
 * Choose from the candidates a rung produced.
 *
 * ⚠️ THE ONLY TIME MORE THAN ONE CANDIDATE RESOLVES is when they are the SAME
 * NAME and exactly one of them carries a `uid`. That is not a choice between
 * two people — it is one person with a duplicate record, which is precisely
 * what the blind-create defect has been manufacturing, and the record with a
 * login is the one that can be messaged, alerted and assigned. It is reported
 * as `duplicateRecords` so the review queue can offer to merge them.
 *
 * Two candidates with DIFFERENT names never resolve, whatever their uids.
 * Preferring the one with a login there would have attached "Bennett" to
 * whichever of two colleagues happened to have signed in.
 */
function pickOne(candidates) {
  if (!candidates.length) return { pick: null, duplicateRecords: false };
  if (candidates.length === 1) return { pick: candidates[0], duplicateRecords: false };
  const names = new Set(candidates.map((c) => norm(c.name)));
  if (names.size === 1) {
    const withUid = candidates.filter((c) => c.uid);
    if (withUid.length === 1) return { pick: withUid[0], duplicateRecords: true };
  }
  return { pick: null, duplicateRecords: false };
}

const resolved = (pick, how, extra = {}) => ({
  status: "resolved",
  staffId: pick.id ?? null,
  uid: pick.uid ?? null,
  name: pick.name ?? "",
  staff: pick,
  how,
  confidence: IDENTITY_CONFIDENCE[how] ?? 0,
  reason: "",
  candidates: [pick],
  ...extra,
});

const unresolved = (reason, candidates, normalised, how = null) => ({
  status: "unresolved",
  staffId: null,
  uid: null,
  name: "",
  staff: null,
  how,
  confidence: 0,
  reason,
  candidates,
  normalised,
});

/** The staff records an alias points at — an alias to a deleted record is dead. */
function aliasCandidates(key, aliases, staff, facilityId) {
  const hits = aliases.filter((a) => {
    if (!a || norm(a.normalised || a.raw) !== key) return false;
    if (!facilityId || !a.facilityId) return true;
    return a.facilityId === facilityId;
  });
  const ids = new Set(hits.map((a) => a.staffId).filter(Boolean));
  return { hits, candidates: staff.filter((p) => ids.has(p.id)) };
}

/**
 * Resolve one name written on the sheet to exactly one staff record, or to
 * nothing.
 *
 * @param {string} rawName  the name as the cell wrote it, already separated
 *   from any co-provider. NOTE: deciding how many people are in a cell is a
 *   different decision, made upstream — this function is asked about ONE name.
 * @param {object} ctx
 *   @param {Array<{id:string, name:string, uid?:string}>} ctx.staff   the pool
 *   @param {Array<object>} [ctx.aliases]      staff_aliases docs
 *   @param {Map} [ctx.qgenda]                 qgendaDisplayIndex(...) result
 *   @param {string} [ctx.facilityId]          scopes the alias rung
 * @returns {{
 *   status: "resolved"|"unresolved",
 *   staffId: string|null, uid: string|null, name: string,
 *   staff: object|null,
 *   how: "alias"|"qgenda"|"exact"|"nickname"|"prefix"|null,
 *   confidence: number,
 *   reason: ""|"empty"|"no-candidate"|"ambiguous",
 *   candidates: Array<object>,
 *   normalised: string,
 *   via?: string, duplicateRecords?: boolean, staleAlias?: boolean
 * }}
 */
/**
 * "Kayla G" → { given: "kayla", initial: "g" }, or null when the name is not
 * written that way.
 *
 * ⚠️ THE INITIAL IS THE LAST TOKEN AND THE GIVEN NAME IS EXACTLY ONE WORD.
 * A leading initial ("J Larson") returns null — that match is refused on
 * purpose everywhere in this app — and so does a two-word given name, because
 * "Sarabel Mercado T" would then bind on "Sarabel" alone.
 */
function surnameInitialParts(rawName) {
  const m = String(rawName || "").trim().match(/^(.*\S)\s+([A-Za-z])\.?$/);
  if (!m) return null;
  const parts = nameParts(m[1]);
  if (parts.length !== 1) return null;
  return { given: parts[0], initial: m[2].toLowerCase() };
}

/**
 * Is this NAME itself only an abbreviation — "Rebecca S", "Shannon M"?
 *
 * ⚠️ ON A STAFF RECORD THIS IS NOT AN IDENTITY, IT IS AN IMPORT ARTEFACT.
 * Read off the live roster on 2026-09-07 there are five, every one of them
 * `uid: null`:
 *
 *     Maria M · Liz B · Olivia M · Rebecca S · Shannon M
 *
 * `Rebecca S` was created 2026-06-30 by an import of the cell
 * "Rebecca S 3 from MRI then to TEE" — the very cell this ladder is now meant
 * to resolve to Rebecca Sigler, who is in the same roster WITH a uid and a
 * phone number. Because the exact rung outranks every fuzzy one, the
 * abbreviation matched the ARTEFACT exactly and the room was handed to a
 * record nobody can log in as, be messaged at, or be given a break from.
 */
export function isAbbreviatedName(name) {
  const parts = String(name || "").trim().split(/\s+/);
  return parts.length === 2 && /^[A-Za-z]\.?$/.test(parts[1]);
}

export function resolveIdentity(rawName, ctx = {}) {
  const staff = (Array.isArray(ctx.staff) ? ctx.staff : []).filter((p) => p && p.id && p.name);
  const aliases = Array.isArray(ctx.aliases) ? ctx.aliases : [];
  const qgenda = ctx.qgenda instanceof Map ? ctx.qgenda : null;
  const facilityId = ctx.facilityId || null;

  const key = norm(rawName);
  if (!key) return unresolved("empty", [], key);

  // ── 1 · A HUMAN ALREADY ANSWERED THIS ────────────────────────────────
  // Read before anything else: this is what makes the system converge instead
  // of asking the same question every morning.
  const alias = aliasCandidates(key, aliases, staff, facilityId);
  if (alias.candidates.length) {
    const { pick, duplicateRecords } = pickOne(alias.candidates);
    if (pick) return resolved(pick, "alias", { normalised: key, duplicateRecords, via: alias.hits[0]?.raw || rawName });
    return unresolved("ambiguous", alias.candidates, key, "alias");
  }
  // ⚠️ An alias whose staff record no longer exists must NOT resolve, and must
  // not be silent either — the answer a human gave now points at nothing.
  const staleAlias = alias.hits.length > 0;

  // ── 2 · THE WORKBOOK'S OWN IDENTITY MAP ──────────────────────────────
  // `Chip Compton` → `Compton, Kang` → the staff record for Kang Compton. No
  // nickname dictionary can do this; the department publishes it daily.
  if (qgenda) {
    const legal = qgenda.get(key);
    if (legal?.legalName) {
      const legalKey = norm(legal.legalName);
      let cands = staff.filter((p) => norm(p.name) === legalKey);
      if (!cands.length) cands = staff.filter((p) => sameHuman(legal.legalName, p.name));
      if (cands.length) {
        const { pick, duplicateRecords } = pickOne(cands);
        if (pick) {
          return resolved(pick, "qgenda", {
            normalised: key, duplicateRecords, staleAlias, via: legal.legalName,
          });
        }
        return unresolved("ambiguous", cands, key, "qgenda");
      }
    }
  }

  // ── 3 · EXACT ────────────────────────────────────────────────────────
  //
  // ⚠️ AN ABBREVIATION MATCHING EXACTLY IS NOT A MATCH, IT IS THE SAME
  // ARTEFACT TWICE. "Rebecca S" on the sheet and "Rebecca S" in the roster
  // agree on nothing except that neither says who she is — and one of them was
  // created BY this importer from the other. Letting that win outranked the
  // real, uid-linked Rebecca Sigler sitting beside it. So an abbreviated query
  // skips straight past exact equality to the rung that can actually expand it,
  // and if that rung refuses, a human is asked.
  const abbreviatedQuery = isAbbreviatedName(rawName);
  const exact = abbreviatedQuery ? [] : staff.filter((p) => norm(p.name) === key);
  if (exact.length) {
    const { pick, duplicateRecords } = pickOne(exact);
    if (pick) return resolved(pick, "exact", { normalised: key, duplicateRecords, staleAlias });
    return unresolved("ambiguous", exact, key, "exact");
  }

  // ── 4 · THE SHIPPED NICKNAME TABLE ───────────────────────────────────
  // Bob→Robert, Jim→James, Femi→Oluwafemi: surname equal AND the given names
  // fold together. Reused, not re-declared — canonicalPerson is where this
  // department's evidenced familiar forms live.
  const nick = staff.filter((p) => sameHuman(rawName, p.name));
  if (nick.length) {
    const { pick, duplicateRecords } = pickOne(nick);
    if (pick) return resolved(pick, "nickname", { normalised: key, duplicateRecords, staleAlias });
    return unresolved("ambiguous", nick, key, "nickname");
  }

  // ── 5 · SURNAME + GIVEN-NAME PREFIX, ONLY WHEN UNIQUE ────────────────
  // `Dom Pearson` → Dominique; `Dimi Borissova` → Dimitrina; `Titi Somade` →
  // Titilope. The surname must match exactly; the prefix rule only ever runs on
  // the given name, so it can never merge two different surnames. `Sam` in a
  // roster with five Samanthas produces five candidates and therefore no answer.
  const surname = surnameOf(rawName);
  const given = first(rawName);
  if (surname && given.length >= MIN_PREFIX) {
    const pre = staff.filter((p) => {
      if (surnameOf(p.name) !== surname) return false;
      const theirs = first(p.name);
      if (!theirs) return false;
      return theirs.startsWith(given) || given.startsWith(theirs);
    });
    if (pre.length) {
      const { pick, duplicateRecords } = pickOne(pre);
      if (pick) return resolved(pick, "prefix", { normalised: key, duplicateRecords, staleAlias });
      return unresolved("ambiguous", pre, key, "prefix");
    }
  }

  // ── 6 · GIVEN NAME + A SURNAME INITIAL, ONLY WHEN UNIQUE ─────────────
  //
  // ⚠️ THE SPELLING THAT WAS LOSING TRAINEES. The Assignment Sheet abbreviates
  // every orientee's surname: `Kayla G (2)`, `Kaylene L (1)`, `Adam B`,
  // `Jodi-Ann R 5`, and `Rebecca S` for a CRNA it names in full three rows
  // above. Rung 5 cannot reach them — it requires the surname to match
  // EXACTLY, and `nameParts` drops a one-letter token before it ever gets
  // there, so `surnameOf("Kayla G")` is "".
  //
  // ⚠️ THIS IS THE MIRROR OF RUNG 5, NOT A SECOND FUZZY MATCH STACKED ON ONE.
  // The given name must be EXACT (no prefix folding, no nickname folding) and
  // there must be exactly ONE of it — because a single letter is the least
  // safe thing in this roster to match on. Two Bennetts, two Patels and five
  // Samanthas are all on file; `Casey B` with a Bennett and a Braun in the
  // pool is an `ambiguous` outcome naming both, never a pick.
  //
  // Lowest rung on purpose: a full name never reaches it.
  const si = surnameInitialParts(rawName);
  if (si) {
    const matches = staff.filter((p) => {
      const parts = nameParts(p.name);
      if (parts.length < 2) return false;
      if (parts[0] !== si.given) return false;
      return parts[parts.length - 1].startsWith(si.initial);
    });
    // ⚠️ A CANDIDATE THAT IS ITSELF AN ABBREVIATION ADDS NOTHING. It repeats the
    // question back. Dropped whenever a real name is standing beside it, so the
    // phantom "Rebecca S" cannot make the genuine Rebecca Sigler ambiguous —
    // which is exactly what it did before this line existed.
    const full = matches.filter((p) => !isAbbreviatedName(p.name));
    const byInitial = full.length ? full : matches;
    if (byInitial.length) {
      const { pick, duplicateRecords } = pickOne(byInitial);
      if (pick) return resolved(pick, "surnameInitial", { normalised: key, duplicateRecords, staleAlias });
      return unresolved("ambiguous", byInitial, key, "surnameInitial");
    }
  }

  // ── UNRESOLVED ───────────────────────────────────────────────────────
  // Everyone on this surname is offered as context for the human who will
  // settle it — a review item that says "nobody" is far harder to action than
  // one that says "there are two Bennetts and neither is a Dave".
  const nearby = surname ? staff.filter((p) => surnameOf(p.name) === surname) : [];
  return { ...unresolved("no-candidate", nearby, key), staleAlias };
}
